import React, { useEffect, useState } from "react";
import { useAuth, useClerk } from "@clerk/clerk-react";
import {
  deleteUserAccount,
  getUserPreferences,
  updateUserPreferences,
} from "../services/storageService";
import { ConfirmationModal, ErrorModal } from "./Modals";
import { Bell, Mail, Monitor, Moon, ShieldAlert, Sun } from "./Icons";
import { useTheme } from "./ThemeProvider";
import { useNotifications } from "./NotificationsProvider";

export const UserSettings: React.FC = () => {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { theme, setTheme } = useTheme();
  const { hasPermission, requestPermission } = useNotifications();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preferences, setPreferences] = useState({
    emailInvitations: true,
    pushInvitations: true,
    emailTaskAssignments: false,
    pushTaskAssignments: true,
  });
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const token = await getToken();
        const data = await getUserPreferences(token || undefined);
        setPreferences(data);
      } catch (e) {
        console.error("Failed to fetch preferences", e);
      }
    };
    fetchPrefs();
  }, [getToken]);

  const handleTogglePref = async (key: keyof typeof preferences) => {
    const newPrefs = { ...preferences, [key]: !preferences[key] };
    setPreferences(newPrefs);
    setIsSavingPrefs(true);
    try {
      const token = await getToken();
      await updateUserPreferences(newPrefs, token || undefined);
    } catch (e) {
      setError("Failed to save preferences");
      // Revert on error
      setPreferences(preferences);
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const token = await getToken();
      await deleteUserAccount(token || undefined);
      await signOut();
      window.location.href = "/";
    } catch (e) {
      setError("Failed to delete account: " + e);
    }
  };

  const PreferenceRow = ({
    label,
    description,
    emailValue,
    pushValue,
    onToggleEmail,
    onTogglePush,
  }: {
    label: string;
    description: string;
    emailValue: boolean;
    pushValue: boolean;
    onToggleEmail: () => void;
    onTogglePush: () => void;
  }) => (
    <div className="flex items-center justify-between py-4">
      <div>
        <p className="text-sm font-medium text-slate-900 dark:text-white">
          {label}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {description}
        </p>
      </div>
      <div className="flex items-center gap-4 sm:gap-8 mr-2">
        <div className="w-12 flex justify-center">
          <button
            onClick={onTogglePush}
            title={pushValue ? "Disable In-app alerts" : "Enable In-app alerts"}
            className={`p-2 rounded-lg transition-all hover:bg-slate-100 dark:hover:bg-slate-800 ${
              pushValue
                ? "text-indigo-600 dark:text-indigo-400"
                : "text-slate-300 dark:text-slate-600"
            }`}
          >
            <Bell
              size={20}
              fill={pushValue ? "currentColor" : "none"}
              className={pushValue ? "fill-indigo-600/20" : ""}
            />
          </button>
        </div>
        <div className="w-12 flex justify-center">
          <button
            onClick={onToggleEmail}
            title={emailValue ? "Disable Email alerts" : "Enable Email alerts"}
            className={`p-2 rounded-lg transition-all hover:bg-slate-100 dark:hover:bg-slate-800 ${
              emailValue
                ? "text-indigo-600 dark:text-indigo-400"
                : "text-slate-300 dark:text-slate-600"
            }`}
          >
            <Mail
              size={20}
              fill={emailValue ? "currentColor" : "none"}
              className={emailValue ? "fill-indigo-600/20" : ""}
            />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ConfirmationModal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="Are you sure you want to delete your account? This will permanently delete all your data, personal workspaces, and remove you from all shared workspaces. This action cannot be undone."
        confirmText="Delete My Account"
        confirmVariant="danger"
      />
      <ErrorModal
        isOpen={!!error}
        onClose={() => setError(null)}
        message={error || ""}
      />

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
          Settings
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Manage your personal preferences and account.
        </p>
      </div>

      <div className="space-y-8">
        {/* Notifications */}
        <section className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
            <Bell size={20} className="text-indigo-600 dark:text-indigo-400" />
            Notifications
          </h2>

          <div className="space-y-6">
            {!hasPermission && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800 flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    <Bell
                      size={18}
                      className="text-indigo-600 dark:text-indigo-400"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-indigo-900 dark:text-indigo-200">
                      Browser alerts are disabled
                    </p>
                    <p className="text-xs text-indigo-700/70 dark:text-indigo-300/70">
                      Enable them to receive real-time task notifications.
                    </p>
                  </div>
                </div>
                <button
                  onClick={requestPermission}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition shadow-sm whitespace-nowrap"
                >
                  Enable Browser Alerts
                </button>
              </div>
            )}

            <div className="relative">
              <div className="flex justify-end mb-2 px-2">
                <div className="flex items-center gap-4 sm:gap-8 mr-0 -mb-3 mt-2">
                  <span className="w-12 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    In-App
                  </span>
                  <span className="w-12 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Email
                  </span>
                </div>
              </div>

              <div className="space-y-1 divide-y divide-slate-100 dark:divide-slate-700/50">
                <PreferenceRow
                  label="Task Alerts"
                  description="When a task is ready for your action."
                  pushValue={preferences.pushTaskAssignments}
                  emailValue={preferences.emailTaskAssignments}
                  onTogglePush={() => handleTogglePref("pushTaskAssignments")}
                  onToggleEmail={() => handleTogglePref("emailTaskAssignments")}
                />
                <PreferenceRow
                  label="Workspace Invitations"
                  description="When you are invited to join a new workspace."
                  pushValue={preferences.pushInvitations}
                  emailValue={preferences.emailInvitations}
                  onTogglePush={() => handleTogglePref("pushInvitations")}
                  onToggleEmail={() => handleTogglePref("emailInvitations")}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Appearance */}
        <section className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Moon size={20} className="text-indigo-600 dark:text-indigo-400" />
            Appearance
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Theme Preference
              </label>
              <div className="grid grid-cols-3 gap-4 max-w-lg">
                {[
                  { id: "light", label: "Light", icon: Sun },
                  { id: "dark", label: "Dark", icon: Moon },
                  { id: "auto", label: "System", icon: Monitor },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setTheme(id as any)}
                    className={`flex flex-col items-center gap-3 p-4 rounded-xl border transition-all ${
                      theme === id
                        ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-900 dark:text-indigo-200 ring-1 ring-indigo-600"
                        : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    }`}
                  >
                    <Icon
                      size={24}
                      className={
                        theme === id
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-slate-400"
                      }
                    />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Account Danger Zone */}
        <section className="bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-900/30 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2 flex items-center gap-2">
            <ShieldAlert size={20} />
            Danger Zone
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Permanently delete your account and all associated data. This action
            is irreversible.
          </p>
          <button
            onClick={() => setDeleteConfirm(true)}
            data-testid="delete-account-button"
            className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/30 rounded-md text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition"
          >
            Delete Account
          </button>
        </section>
      </div>
    </div>
  );
};
